/**
 * The lockstep flight runner: the same encounter as `/fly`, stepped on the
 * network's terms instead of the wall clock's.
 *
 * The difference from `sim-runner.ts` is exactly one line of policy — a tick
 * is produced only when `Lockstep` has both players' sticks for it, and the
 * frame gives up its remaining budget the moment it does not. Everything else
 * (fixed-step accumulator, pose interpolation, HUD, rounds, messages) is the
 * single-player path unchanged, which is what makes a duel feel like `/fly`
 * rather than like a second, worse flight model.
 *
 * Dropped time on a stall is deliberate: the alternative is banking it and
 * paying it back as a burst of catch-up ticks, which would make a hitch look
 * like the opponent teleporting. A stall is announced (`waiting()`) and simply
 * costs the fight a few milliseconds on both machines equally.
 */

import {
  FIXED_DT,
  createEncounter,
  encounterHash,
  interpolationAlpha,
  planFixedSteps,
  stepEncounter,
  type EncounterState,
  type FlightInputs,
  type Pose,
  type SimState,
} from "@red-opus/engine";
import {
  HashLedger,
  Lockstep,
  NET_PROTOCOL_VERSION,
  type DuelMessage,
  type DuelSetup,
  type SignalRole,
  type TickRelease,
} from "@red-opus/net";
import { combatHudFrom, type CombatHud } from "../flight/combat-hud.ts";
import { roundViews, type RoundView } from "../flight/encounter-setup.ts";
import {
  aircraftViewsOf,
  effectSpawnsOf,
  playerPoseOf,
  playerSimOf,
  targetViewsOf,
  type AircraftView,
  type EffectSpawn,
  type TargetView,
} from "../flight/encounter-views.ts";
import { hudFrom, type FlightHud } from "../flight/hud-snapshot.ts";
import { encounterMessages } from "../flight/sim-messages.ts";
import type { EncounterRunner, FlightFrame } from "../flight/sim-runner.ts";
import { duelEncounterSetup, localIdFor, remoteIdFor } from "./duel-setup.ts";
import { duelBanner, duelOutcomeOf, isDuelDecided, type DuelOutcome } from "./duel-outcome.ts";

export interface DuelRunnerHooks {
  /** Hand this to the DataChannel. Called from inside `advance`, never asynchronously. */
  readonly onSend: (message: DuelMessage) => void;
  /** Fired once, on the tick a hash pair disagreed. */
  readonly onDesync: (tick: number) => void;
  /** Fired once, on the tick the fight is decided. */
  readonly onDecided: (outcome: DuelOutcome) => void;
}

export interface DuelRunner extends EncounterRunner {
  /** True while the next tick is held up waiting for the opponent's stick. */
  waiting(): boolean;
  outcome(): DuelOutcome;
  /** Ticks simulated. Identical on both peers for any tick both have reached. */
  tick(): number;
  /** Feeds one decoded frame from the peer. */
  accept(message: DuelMessage): void;
  /** Ends the duel from outside — a lost connection, or the player leaving. */
  finish(outcome: DuelOutcome): void;
  /** Scheduled hashes confirmed identical so far. The integrity check's own readout. */
  verified(): number;
}

interface Fight {
  previous: EncounterState;
  current: EncounterState;
  accumulator: number;
  paused: boolean;
  lastMessage: string;
  outcome: DuelOutcome;
}

function openingFight(setup: DuelSetup): Fight {
  const initial = createEncounter(duelEncounterSetup(setup));
  return { previous: initial, current: initial, accumulator: 0, paused: false, lastMessage: "", outcome: "flying" };
}

class LockstepRunner implements DuelRunner {
  private readonly fight: Fight;
  private readonly core: Lockstep;
  private readonly hashes = new HashLedger();
  private readonly localId: string;
  private readonly remoteId: string;

  constructor(setup: DuelSetup, role: SignalRole, private readonly hooks: DuelRunnerHooks) {
    this.localId = localIdFor(role);
    this.remoteId = remoteIdFor(role);
    this.fight = openingFight(setup);
    this.core = new Lockstep({
      localId: this.localId,
      remoteId: this.remoteId,
      inputDelay: setup.inputDelay,
      // Negotiated, not defaulted: two peers checking on different schedules
      // would exchange hashes that never meet, and the integrity check would
      // read as healthy for the whole fight without ever comparing anything.
      hashInterval: setup.hashInterval,
    });
  }

  private alpha(): number {
    return interpolationAlpha(this.fight.accumulator);
  }

  private frozen(): boolean {
    return this.fight.paused || isDuelDecided(this.fight.outcome);
  }

  private judge(tick: number, mine: string): void {
    if (this.hashes.recordLocal(tick, mine) !== "mismatch") return;
    this.hooks.onDesync(this.hashes.divergedAt ?? tick);
    this.finish("desync");
  }

  /** Applies one released tick and reports the HUD lines it produced, appending any explosion sites to `effects`. */
  private applyTick(release: TickRelease, effects: EffectSpawn[]): readonly string[] {
    const previous = this.fight.current;
    const next = stepEncounter(previous, release.inputs, FIXED_DT);
    this.fight.previous = previous;
    this.fight.current = next;
    if (release.hashDue) {
      const mine = encounterHash(next);
      this.hooks.onSend({ t: "hash", v: NET_PROTOCOL_VERSION, tick: release.tick, hash: mine });
      this.judge(release.tick, mine);
    }
    effects.push(...effectSpawnsOf(next));
    const decided = duelOutcomeOf(next, this.localId, this.remoteId);
    if (decided !== "flying") this.finish(decided);
    return encounterMessages(previous, next, this.localId);
  }

  private runFrame(steps: number, inputs: FlightInputs, effects: EffectSpawn[]): readonly string[] {
    const lines: string[] = [];
    for (let index = 0; index < steps; index++) {
      const { send, release } = this.core.advance(inputs);
      if (send) this.hooks.onSend({ t: "inputs", v: NET_PROTOCOL_VERSION, tick: send.tick, runs: send.runs });
      if (!release) break;
      for (const line of this.applyTick(release, effects)) {
        if (line === this.fight.lastMessage) continue;
        this.fight.lastMessage = line;
        lines.push(line);
      }
      if (isDuelDecided(this.fight.outcome)) break;
    }
    return lines;
  }

  advance(frameSeconds: number, inputs: FlightInputs): FlightFrame {
    if (this.frozen()) return { steps: 0, messages: [], effects: [], clamped: false };
    const before = this.core.tick;
    const plan = planFixedSteps(this.fight.accumulator, frameSeconds);
    this.fight.accumulator = plan.accumulator;
    const effects: EffectSpawn[] = [];
    const messages = this.runFrame(plan.steps, inputs, effects);
    return { steps: this.core.tick - before, messages, effects, clamped: plan.clamped };
  }

  pose(): Pose {
    return playerPoseOf(this.fight.previous, this.fight.current, this.localId, this.alpha());
  }

  state(): SimState {
    return playerSimOf(this.fight.current, this.localId);
  }

  hud(): FlightHud {
    return hudFrom(this.state());
  }

  others(): readonly AircraftView[] {
    return aircraftViewsOf(this.fight.previous, this.fight.current, this.localId, this.alpha());
  }

  targets(): readonly TargetView[] {
    return targetViewsOf(this.fight.current);
  }

  rounds(): readonly RoundView[] {
    return roundViews(this.fight.current, this.localId);
  }

  /**
   * The engine's own banner is friendly-vs-hostile and would congratulate the
   * loser; the duel's verdict is seat-relative (`duel-outcome.ts`).
   */
  combat(): CombatHud {
    return { ...combatHudFrom(this.fight.current, this.localId), banner: duelBanner(this.fight.outcome) };
  }

  encounter(): EncounterState {
    return this.fight.current;
  }

  paused(): boolean {
    return this.fight.paused;
  }

  setPaused(paused: boolean): void {
    this.fight.paused = paused;
  }

  waiting(): boolean {
    return this.core.stalledTicks > 0 && !this.frozen();
  }

  outcome(): DuelOutcome {
    return this.fight.outcome;
  }

  tick(): number {
    return this.core.tick;
  }

  verified(): number {
    return this.hashes.matches;
  }

  finish(outcome: DuelOutcome): void {
    if (isDuelDecided(this.fight.outcome)) return;
    this.fight.outcome = outcome;
    this.hooks.onDecided(outcome);
  }

  accept(message: DuelMessage): void {
    if (message.t === "inputs") this.core.acceptRemote(message.tick, message.runs);
    if (message.t === "hash" && this.hashes.recordRemote(message.tick, message.hash) === "mismatch") {
      this.hooks.onDesync(this.hashes.divergedAt ?? message.tick);
      this.finish("desync");
    }
    if (message.t === "end" && message.reason === "quit") this.finish("disconnected");
  }
}

export function createDuelRunner(setup: DuelSetup, role: SignalRole, hooks: DuelRunnerHooks): DuelRunner {
  return new LockstepRunner(setup, role, hooks);
}
